/**
 * Website CMS v4 Schemas
 *
 * Zod schemas for the v4 customer-website CMS content blocks that extend
 * `WebsiteSiteConfig.config` JSONB on `25-tenant-service`. Each top-level key
 * under `WebsiteCmsV4` is an OPTIONAL sub-path so existing content (template
 * pages, branding, footer) is unaffected; new v4 pages opt in by writing to
 * the new sub-paths surgically (see `.claude/rules/data-migrations.md`).
 *
 * Type shape sources of truth:
 * - `.claude/design-handoff-v3/project/pages/whitelabel.jsx` (BRANDS.{coastal,summit,beacon})
 * - `.claude/design-handoff-v3/project/pages/coastal-v4.jsx` (funded deals, leadership, products)
 * - `.claude/design-handoff-v3/project/pages/coastal-v4-pages.jsx` (markets, partners)
 * - `.claude/design-handoff-v3/project/pages/coastal-v4-intake.jsx` (intake form mode + fields)
 *
 * Color fields are raw lowercase 6-char hex strings — these flow to JSON and
 * downstream to CSS, not to MUI theme tokens.
 *
 * See: `.claude/design-handoff-v3/IMPLEMENTATION_PLAN.md` Stream B · B0.
 */

import { z } from 'zod';

// =============================================================================
// PRIMITIVES
// =============================================================================

/**
 * 6-character hex color, case-insensitive (e.g. `#0d8a7a` or `#0D8A7A`).
 *
 * These hex values are CMS content (per-brand palette) that flow into the
 * rendered HTML/CSS at the website portal. They are NOT mapped to MUI
 * `baseColors.*` tokens (the website portal uses its own brand palette).
 */
export const HexColorSchema = z.string().regex(/^#[0-9a-f]{6}$/i, 'Must be a 6-character hex color, e.g. #0d8a7a');

const SlugSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Slug must be lowercase, alphanumeric, with optional hyphens');

const NonEmptyStringSchema = z.string().min(1);

/**
 * Image / asset reference field. Accepts an absolute http(s) URL or a
 * single-leading-slash site-relative path. Rejects bare filenames
 * (`logo.png`), protocol-relative URLs (`//cdn.example/...`), and
 * dangerous non-http(s) schemes (`javascript:`, `data:`).
 *
 * Why a separate primitive instead of `z.string().url()`: the canonical
 * CMS path on prod (e.g. Coastal) is site-relative — assets like
 * `/assets/customers/<tenant>/logo.svg` live under the portal's public
 * tree and `<Image>` resolves them correctly without needing an
 * absolute origin. A strict `.url()` would reject the entire
 * tenant-service config on parse, surfacing as DATA_LOSS errors in
 * upstream callers (e.g. WebsiteCmsService upsert/list).
 *
 * Use this for: brand `logo`, leadership `photo`, funded-deal `photo`,
 * and any future image/asset references that may legitimately be
 * served from the portal's own /public tree.
 *
 * Don't use this for: fully-external references that MUST be absolute
 * (e.g. LinkedIn URLs, third-party CDN URLs) — keep `z.string().url()`
 * there so the strict validation still fires.
 */
const UrlOrPathSchema = z
  .string()
  .min(1)
  .max(2048)
  .refine((v) => /^(https?:\/\/[^\s]+|\/[^/].*)$/.test(v), {
    message: 'must be an absolute http(s) URL or a single leading-slash path (e.g. /assets/.../logo.svg)',
  });

// =============================================================================
// BRAND CONFIG
// =============================================================================

/**
 * Per-tenant brand palette + voice config. Mirrors
 * `BRANDS.coastal` / `BRANDS.summit` / `BRANDS.beacon` in
 * `.claude/design-handoff-v3/project/pages/whitelabel.jsx`.
 *
 * `phone` and `location` are optional because some platform tenants ship
 * without a published phone or geographic anchor at launch; everything else
 * is required so a brand can render the v4 chrome (header / footer / hero)
 * without runtime fallbacks.
 */
export const BrandConfigSchema = z.object({
  /** Stable brand identifier — e.g. `'coastal'`, `'summit'`. */
  id: SlugSchema,
  /** Top wordmark line (uppercase, e.g. `'COASTAL'`). */
  name: NonEmptyStringSchema.max(40),
  /** Bottom wordmark line (uppercase, e.g. `'FUNDING'`). */
  sub: NonEmptyStringSchema.max(40),
  /** Legal / display full name (e.g. `'Coastal Real Estate Funding'`). */
  fullName: NonEmptyStringSchema.max(120),
  /** Optional headquarters anchor (e.g. `'Ft. Myers, FL'`); national brands may omit. */
  location: NonEmptyStringSchema.max(100).optional(),
  /** Hero headline with `italic` substring rendered in accent italic. */
  tagline: NonEmptyStringSchema.max(200),
  /** Sub-string of `tagline` rendered in italic accent color. */
  italic: NonEmptyStringSchema.max(80),
  /** Mono uppercase eyebrow over the hero (avoid geographic anchors for national brands). */
  eyebrow: NonEmptyStringSchema.max(200),

  // Palette — hex strings (flow to CSS, not MUI tokens)
  /** Primary accent (e.g. teal `#0d8a7a`). */
  accent: HexColorSchema,
  /** Darker accent for hover / active states. */
  accentDark: HexColorSchema,
  /** Soft accent tint for backgrounds. */
  accentSoft: HexColorSchema,
  /** Accent border for outlined accent surfaces. */
  accentBorder: HexColorSchema,
  /** Body text + dark surfaces (navy / charcoal). */
  ink: HexColorSchema,
  /** Page background (near-white). */
  bg: HexColorSchema,
  /** Warm background for callout sections. */
  bgWarm: HexColorSchema,
  /** Sand-tone background for hero secondary panels. */
  bgSand: HexColorSchema,
  /** Sand accent for sub-surfaces / portrait gradients. */
  sand: HexColorSchema,

  /** Optional public phone number — e.g. `'(239) 259-9829'`. */
  phone: NonEmptyStringSchema.max(40).optional(),
  /** Bare apex domain — e.g. `'coastal-funding.com'`. */
  domain: NonEmptyStringSchema.max(253),
  /** Long-form brand voice paragraph used in manifesto + about copy. */
  body: NonEmptyStringSchema.max(2000),
  /**
   * Optional brand-image logo URL. When set, the v4 chrome (header /
   * footer) renders the asset via `<Image>` instead of the
   * `StackedWordmark` typographic lockup. Tenants migrating from v3 to
   * v4 typically already have a logo at `siteConfig.branding.logo`; the
   * dispatcher composes `cmsV4.brand.logo` from that fallback so chrome
   * doesn't regress to a re-typed wordmark when the v4 brand object
   * forgot to copy the image URL across.
   *
   * Accepts an absolute URL (`https://cdn.example/logo.svg`) or a
   * site-relative path (`/assets/customers/<tenant>/logo.png`). See
   * `UrlOrPathSchema` above for the rationale — the canonical v3 path
   * on prod is site-relative, so a strict `.url()` would reject every
   * tenant config at parse time.
   */
  logo: UrlOrPathSchema.optional(),
});

export type BrandConfig = z.infer<typeof BrandConfigSchema>;

// =============================================================================
// FUNDED DEALS
// =============================================================================

/**
 * Discriminator for the kind of loan a funded-deal card represents.
 * Matches the v4 `DealCard` `type` field; extend as new products ship.
 */
export const FundedDealPropertyTypeSchema = z.enum(['fix-flip', 'dscr', 'bridge', 'construction', 'commercial']);

export type FundedDealPropertyType = z.infer<typeof FundedDealPropertyTypeSchema>;

/**
 * A single closed-deal card in the deal-feed section of the v4 homepage.
 * Maps to `pages/coastal-v4.jsx` `DealCard` artboard.
 *
 * `amount` is in USD dollars (not cents). `ltv` is a fraction in `[0, 1]`,
 * e.g. `0.7` for 70%. `closedAt` is an ISO-8601 calendar date (`YYYY-MM-DD`).
 */
export const FundedDealSchema = z.object({
  /** URL-stable identifier — e.g. `'crf-2026-142'`. */
  slug: SlugSchema,
  /** Display headline — `'Oakland Fix & Flip · 7 days'`. */
  headline: NonEmptyStringSchema.max(160),
  /** Loan amount in USD (dollars, not cents). */
  amount: z.number().int().positive().max(100_000_000),
  /** Loan-to-value as a fraction in [0, 1] (e.g. 0.7 = 70%). */
  ltv: z.number().min(0).max(1),
  /** Calendar days from inquiry to wire. */
  daysToClose: z.number().int().min(0).max(365),
  /** ISO calendar date the deal closed (e.g. `'2026-05-13'`). */
  closedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD'),
  /** Loan-product discriminator. */
  propertyType: FundedDealPropertyTypeSchema,
  /**
   * Optional property photo. Site-relative path (canonical Coastal
   * shape, e.g. `/assets/customers/coastal/deals/oakland.jpg`) or an
   * absolute http(s) URL. Null when no photo is on file. See
   * `UrlOrPathSchema` for why this isn't a strict `.url()`.
   */
  photo: UrlOrPathSchema.nullable(),
  /** Rich-text summary (plain text or markdown) for the card body. */
  summary: NonEmptyStringSchema.max(2000),
});

export type FundedDeal = z.infer<typeof FundedDealSchema>;

// =============================================================================
// LEADERSHIP BIOS
// =============================================================================

/**
 * A single stat row in the per-founder stats strip on the v4 `/leadership`
 * page (e.g. `{ value: '$2B', label: 'Underwritten' }`). Mirrors the
 * 3-column stats grid in `pages/coastal-v4-pages.jsx` `CoastalLeadershipV4`.
 */
export const LeadershipStatSchema = z.object({
  /** Display value (`'$2B'`, `'36,000'`, `'20+'`). */
  value: NonEmptyStringSchema.max(40),
  /** Mono-uppercase label below the value (`'Underwritten'`, `'Years'`). */
  label: NonEmptyStringSchema.max(80),
});

export type LeadershipStat = z.infer<typeof LeadershipStatSchema>;

/**
 * A leadership / co-founder bio card on the v4 `/leadership` page.
 * Maps to `pages/coastal-v4-pages.jsx` `CoastalLeadershipV4`.
 */
export const LeadershipBioSchema = z.object({
  /** URL slug — `'david-diaz'`. */
  slug: SlugSchema,
  /** Display name. */
  name: NonEmptyStringSchema.max(120),
  /** Role / title (e.g. `'Co-Founder · CEO'`). */
  title: NonEmptyStringSchema.max(160),
  /**
   * Rich-text bio. Either a single string (legacy shape — paragraphs split
   * on `\n\n` at render time) or an explicit `string[]` of paragraphs (new
   * shape — recommended for editorial copy with three or more paragraphs
   * per founder so the CMS author controls the breaks instead of relying
   * on whitespace conventions). The renderer normalizes both shapes into
   * an ordered list of `<p>` blocks.
   */
  bio: z.union([NonEmptyStringSchema.max(8000), z.array(NonEmptyStringSchema.max(4000)).min(1).max(12)]),
  /**
   * Optional headshot. Site-relative path (canonical Coastal shape,
   * e.g. `/assets/customers/coastal/leadership/david-diaz.jpg`) or an
   * absolute http(s) URL. Null when only the initials chip renders.
   * See `UrlOrPathSchema` for why this isn't a strict `.url()`.
   */
  photo: UrlOrPathSchema.nullable(),
  /**
   * Optional LinkedIn profile URL. Stays strict `.url()` — LinkedIn
   * profile links are always absolute (`https://www.linkedin.com/in/...`),
   * so the site-relative carve-out doesn't apply.
   */
  linkedin: z.string().url().optional(),
  /**
   * Optional mono-uppercase tagline shown ABOVE the founder's name in the
   * leadership grid (`'The builder.'` / `'The structurer.'`). Per the
   * coastal-v4-pages.jsx design spec — accent-colored, 11px Fraunces mono.
   */
  tagline: NonEmptyStringSchema.max(120).optional(),
  /**
   * Optional 3-column stats strip rendered between the title and the bio
   * paragraphs. Each entry is a `{value, label}` pair. Up to four rows are
   * supported so a brand can ship a 4-column variant if the design evolves
   * without another schema bump.
   */
  stats: z.array(LeadershipStatSchema).min(1).max(4).optional(),
});

export type LeadershipBio = z.infer<typeof LeadershipBioSchema>;

// =============================================================================
// MARKET REGIONS
// =============================================================================

/**
 * Aggregate stats for a market-region card (`/markets` page).
 * `fundedTotal` is USD dollars. `avgDaysToClose` is a calendar-day count.
 */
export const MarketRegionStatsSchema = z.object({
  fundedTotal: z.number().int().min(0),
  dealCount: z.number().int().min(0),
  avgDaysToClose: z.number().min(0).max(365),
});

export type MarketRegionStats = z.infer<typeof MarketRegionStatsSchema>;

/**
 * A regional card on the v4 `/markets` page. Maps to
 * `pages/coastal-v4-pages.jsx` `CoastalMarketsV4` (lines 440-457).
 *
 * The v4 grid card body shows: region name (h3), summary paragraph,
 * two mono caption rows (`Key markets:` and `Counties:`), and an
 * accent quote CTA. The 2-col grid layout pulls these per-region
 * details into a dense featured-markets section.
 *
 * `markets` / `counties` / `ctaLabel` are OPTIONAL so existing
 * regional cards (only `name` / `headline` / `summary` / `stats`)
 * continue to render — the v4 page gracefully omits the mono rows
 * when the fields are absent.
 *
 * `stats` was the original v3-era footer block. v4 grid cards prefer
 * the narrative key-markets / counties layout over the stats strip;
 * making `stats` optional lets the new 6-region featured-markets
 * layout omit synthetic numerics that don't exist for sub-regions.
 *
 * `headline` is optional in the new grid layout (the page hero carries
 * the global headline; per-card headlines are a v3 affordance).
 */
export const MarketRegionSchema = z.object({
  /** URL slug — `'south-florida'`, `'nationwide'`. */
  slug: SlugSchema,
  /** Display region name. */
  name: NonEmptyStringSchema.max(120),
  /** Optional card headline (e.g. `'Where we lend & how we know it.'`). */
  headline: NonEmptyStringSchema.max(200).optional(),
  /** Card body paragraph. */
  summary: NonEmptyStringSchema.max(2000),
  /** Aggregate stats shown in the card footer. Optional for v4 grid cards. */
  stats: MarketRegionStatsSchema.optional(),
  /** Optional `Key markets:` mono caption — e.g. `'Miami · Fort Lauderdale · West Palm Beach'`. */
  markets: NonEmptyStringSchema.max(400).optional(),
  /** Optional `Counties:` mono caption — e.g. `'Miami-Dade · Broward · Palm Beach'`. */
  counties: NonEmptyStringSchema.max(400).optional(),
  /** Optional CTA label override (defaults to `'Quote a deal in <firstWordOfName>'`). */
  ctaLabel: NonEmptyStringSchema.max(120).optional(),
});

export type MarketRegion = z.infer<typeof MarketRegionSchema>;

// =============================================================================
// PARTNER PROGRAM
// =============================================================================

/**
 * One sample row inside the `attribution` block on the v4 `/partners`
 * page. Renders as a single team-member row with name + role, referral
 * link (mono, accent-tinted background), conversion count, and earned
 * USD display. Maps to `pages/coastal-v4-pages.jsx` lines 585-599.
 *
 * The sample table is a SAMPLE — it illustrates the per-employee
 * attribution flow rather than displaying live data. Three rows is the
 * canonical preview length per the design.
 */
export const PartnerAttributionSampleRowSchema = z.object({
  /** Display name — `'Daniel R.'`. Privacy-preserving (last-initial). */
  name: NonEmptyStringSchema.max(80),
  /** Role / title — `'Sr. Originator'`. */
  role: NonEmptyStringSchema.max(80),
  /** Referral link — `'coastal-funding.com/r/dr'`. */
  link: NonEmptyStringSchema.max(200),
  /** Conversion count (e.g. 12). */
  conv: z.number().int().min(0).max(99_999),
  /** Earnings display string — `'$24.1k'`. */
  earn: NonEmptyStringSchema.max(40),
});

export type PartnerAttributionSampleRow = z.infer<typeof PartnerAttributionSampleRowSchema>;

/**
 * `attribution` sub-block — the "Every employee gets their own link"
 * section of the v4 `/partners` page. Maps to
 * `pages/coastal-v4-pages.jsx` lines 578-601.
 *
 * Renders as a dark V4Section with an explanatory paragraph + a sample
 * team-member table demonstrating per-employee referral attribution.
 * Optional on the parent `PartnerProgram` — pages that haven't opted
 * into the per-employee attribution narrative just hide the section.
 */
export const PartnerAttributionBlockSchema = z.object({
  /** Section eyebrow — e.g. `'ATTRIBUTION'`. */
  eyebrow: NonEmptyStringSchema.max(80),
  /** Section headline — `'Every employee gets'`. */
  headline: NonEmptyStringSchema.max(200),
  /** Optional italic accent substring of headline — `'their own link.'`. */
  italic: NonEmptyStringSchema.max(120).optional(),
  /** Explanatory body paragraph under the headline. */
  body: NonEmptyStringSchema.max(800),
  /** Label rendered above the sample table — `'YOUR TEAM · SAMPLE'`. */
  sampleLabel: NonEmptyStringSchema.max(80),
  /** Sample team-member rows (1–8). */
  sample: z.array(PartnerAttributionSampleRowSchema).min(1).max(8),
});

export type PartnerAttributionBlock = z.infer<typeof PartnerAttributionBlockSchema>;

/**
 * The single partner-program block rendered on `/partners`. Maps to
 * `pages/coastal-v4-pages.jsx` `CoastalPartnersV4` (lines 545-614).
 *
 * `attribution` is optional — pages that haven't opted into the
 * "Every employee gets their own link" section hide it. `benefits`
 * and `requirements` are also optional now (the v4 grid layout
 * dropped the parallel-bullet-list § 02 in favor of the bidirectional
 * flow + attribution + apply-CTA chapter sequence).
 */
export const PartnerProgramSchema = z.object({
  /** URL slug — `'broker-program'`. */
  slug: SlugSchema,
  /** Page headline (e.g. `'Refer borrowers. Earn alongside us.'`). */
  headline: NonEmptyStringSchema.max(200),
  /** Sub-headline / eyebrow text. */
  sub: NonEmptyStringSchema.max(300),
  /** Optional bulleted list of partner benefits. */
  benefits: z.array(NonEmptyStringSchema.max(400)).max(12).optional(),
  /** Optional bulleted list of partner requirements. */
  requirements: z.array(NonEmptyStringSchema.max(400)).max(12).optional(),
  /** Optional per-employee attribution sub-section. */
  attribution: PartnerAttributionBlockSchema.optional(),
  /** Call-to-action button label (e.g. `'Apply to partner'`). */
  applyCta: NonEmptyStringSchema.max(80),
});

export type PartnerProgram = z.infer<typeof PartnerProgramSchema>;

// =============================================================================
// INTAKE FORM CONFIG
// =============================================================================

/**
 * Intake-form rendering mode.
 * - `page-by-page` — one section per page with a step bar.
 * - `infinite-scroll` — all sections rendered on one page with sidebar nav.
 *
 * NOTE: `Website*` prefix on the website-CMS intake exports avoids collision
 * with the long-standing `Intake*` template types in `1-shared-types/src/templates/`
 * (loan-intake-flow templates, fields, validation rules — Issues #432 / #471 /
 * #526). Those serve the in-app loan-wizard; these serve the public website.
 */
export const WebsiteIntakeFormModeSchema = z.enum(['page-by-page', 'infinite-scroll']);
export type WebsiteIntakeFormMode = z.infer<typeof WebsiteIntakeFormModeSchema>;

/**
 * Intake-form light/dark variant.
 *
 * Named `*ThemeMode` (not just `Theme`) because `templates/types.ts` already
 * exports a rich `IntakeFormTheme` interface (light/dark overrides, color
 * stops). This is just the discriminator flag.
 */
export const WebsiteIntakeFormThemeModeSchema = z.enum(['light', 'dark']);
export type WebsiteIntakeFormThemeMode = z.infer<typeof WebsiteIntakeFormThemeModeSchema>;

/**
 * One radio / select option. `value` is the storable string;
 * `label` is the rendered chip.
 */
export const WebsiteIntakeFieldOptionSchema = z.object({
  value: NonEmptyStringSchema.max(120),
  label: NonEmptyStringSchema.max(160),
  /** Optional sub-text shown under the label (radio cards). */
  sub: z.string().max(400).optional(),
  /** Optional `BEST FIT` chip — only one option per field should set this. */
  recommended: z.boolean().optional(),
});
export type WebsiteIntakeFieldOption = z.infer<typeof WebsiteIntakeFieldOptionSchema>;

/**
 * Base fields shared by every intake-field variant.
 *
 * `id` is the storable key (e.g. `'purchasePrice'`); `label` is rendered.
 * `hint` is optional helper copy under the input.
 */
const websiteIntakeFieldBase = {
  id: NonEmptyStringSchema.max(80).regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, 'Field id must be a JS-identifier-style key'),
  label: NonEmptyStringSchema.max(200),
  required: z.boolean().optional(),
  hint: z.string().max(400).optional(),
  placeholder: z.string().max(200).optional(),
};

/** Discriminator value list. */
export const WebsiteIntakeFieldKindSchema = z.enum(['text', 'number', 'select', 'radio', 'file']);
export type WebsiteIntakeFieldKind = z.infer<typeof WebsiteIntakeFieldKindSchema>;

/** Free-text field (e.g. borrower name, exit strategy). */
export const WebsiteIntakeTextFieldSchema = z.object({
  ...websiteIntakeFieldBase,
  kind: z.literal('text'),
  /** Optional regex pattern to enforce on submit. */
  pattern: z.string().max(500).optional(),
  /** Optional max input length (UI hint + server validation). */
  maxLength: z.number().int().min(1).max(10_000).optional(),
});
export type WebsiteIntakeTextField = z.infer<typeof WebsiteIntakeTextFieldSchema>;

/** Numeric field — e.g. purchase price, FICO score. */
export const WebsiteIntakeNumberFieldSchema = z.object({
  ...websiteIntakeFieldBase,
  kind: z.literal('number'),
  /** Optional `$` / `%` prefix shown in the input chrome. */
  prefix: z.string().max(8).optional(),
  /** Optional unit suffix (e.g. `'months'`). */
  suffix: z.string().max(16).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
});
export type WebsiteIntakeNumberField = z.infer<typeof WebsiteIntakeNumberFieldSchema>;

/** Single-select dropdown / radio group. */
export const WebsiteIntakeSelectFieldSchema = z.object({
  ...websiteIntakeFieldBase,
  kind: z.literal('select'),
  options: z.array(WebsiteIntakeFieldOptionSchema).min(1).max(40),
});
export type WebsiteIntakeSelectField = z.infer<typeof WebsiteIntakeSelectFieldSchema>;

/** Vertical radio cards (e.g. loan-type selector). */
export const WebsiteIntakeRadioFieldSchema = z.object({
  ...websiteIntakeFieldBase,
  kind: z.literal('radio'),
  options: z.array(WebsiteIntakeFieldOptionSchema).min(2).max(12),
});
export type WebsiteIntakeRadioField = z.infer<typeof WebsiteIntakeRadioFieldSchema>;

/** File upload field — e.g. ID, bank statement, rent roll. */
export const WebsiteIntakeFileFieldSchema = z.object({
  ...websiteIntakeFieldBase,
  kind: z.literal('file'),
  /** Allowed MIME types or extensions; `[]` means any. */
  accept: z.array(z.string().max(80)).max(20).optional(),
  /** Allow multiple files (default: false). */
  multiple: z.boolean().optional(),
  /** Max size per file in bytes. */
  maxSizeBytes: z.number().int().positive().max(500_000_000).optional(),
});
export type WebsiteIntakeFileField = z.infer<typeof WebsiteIntakeFileFieldSchema>;

/**
 * Union of every website-intake-field variant. Discriminated by `kind` so
 * downstream code can switch on it without `as` casts.
 */
export const WebsiteIntakeFieldSchema = z.discriminatedUnion('kind', [
  WebsiteIntakeTextFieldSchema,
  WebsiteIntakeNumberFieldSchema,
  WebsiteIntakeSelectFieldSchema,
  WebsiteIntakeRadioFieldSchema,
  WebsiteIntakeFileFieldSchema,
]);
export type WebsiteIntakeField = z.infer<typeof WebsiteIntakeFieldSchema>;

/**
 * One section of an intake form — a logical group of fields (e.g.
 * `'Deal Financials'`, `'Borrower Information'`). Maps to the sidebar nav
 * entries in `pages/coastal-v4-intake.jsx` `CoastalIntakeScrollV4`.
 */
export const WebsiteIntakeFormSectionSchema = z.object({
  /** Stable section id used in the URL and progress bar. */
  id: NonEmptyStringSchema.max(80).regex(/^[a-zA-Z][a-zA-Z0-9_-]*$/, 'Section id must be a slug-style key'),
  /** Display title (e.g. `'Deal Financials'`). */
  title: NonEmptyStringSchema.max(160),
  /** Optional sub-headline rendered next to the title in sidebar nav. */
  sub: z.string().max(400).optional(),
  /** Ordered list of fields in this section. */
  fields: z.array(WebsiteIntakeFieldSchema).min(1).max(60),
});
export type WebsiteIntakeFormSection = z.infer<typeof WebsiteIntakeFormSectionSchema>;

/**
 * Top-level website intake-form configuration. Drives the page-by-page
 * wizard, the infinite-scroll variant, and the embed widget (`/embed/intake`).
 *
 * `productList` is the set of loan-product slugs offered on the loan-type
 * step (e.g. `['fix-flip', 'construction', 'bridge', 'dscr']`). Empty list =
 * single-product brand.
 */
export const WebsiteIntakeFormConfigSchema = z.object({
  mode: WebsiteIntakeFormModeSchema,
  theme: WebsiteIntakeFormThemeModeSchema,
  /** Loan-product slugs offered on the loan-type step. */
  productList: z.array(SlugSchema).max(20),
  /** Ordered sections; first section is rendered first. */
  sections: z.array(WebsiteIntakeFormSectionSchema).min(1).max(20),
});
export type WebsiteIntakeFormConfig = z.infer<typeof WebsiteIntakeFormConfigSchema>;

// =============================================================================
// TOP-LEVEL CMS V4 SCHEMA
// =============================================================================

/**
 * Top-level v4 CMS content carried inside `WebsiteSiteConfig.config` (the
 * JSONB column owned by `25-tenant-service`). All keys are OPTIONAL — a
 * tenant that has not yet adopted v4 simply omits them, and the existing
 * `branding` / `pages` / `footer` content keeps rendering through the v3
 * template engine.
 *
 * Per `.claude/rules/data-migrations.md`, writes to these sub-paths must
 * use surgical `jsonb_set` (never full-column replace).
 */
export const WebsiteCmsV4Schema = z.object({
  brand: BrandConfigSchema.optional(),
  fundedDeals: z.array(FundedDealSchema).max(200).optional(),
  leadership: z.array(LeadershipBioSchema).max(40).optional(),
  markets: z.array(MarketRegionSchema).max(60).optional(),
  partners: PartnerProgramSchema.optional(),
  intakeForm: WebsiteIntakeFormConfigSchema.optional(),
});

export type WebsiteCmsV4 = z.infer<typeof WebsiteCmsV4Schema>;
